// /var/www/oszczedzam/client/src/pages/Reports.js
import React, { useState, useEffect } from 'react';
import { 
  Box, Typography, Paper, Tabs, Tab, CircularProgress, Alert,
  Grid, Divider, FormControl, InputLabel, Select, MenuItem
} from '@mui/material';
import { useTransactions } from '../hooks/useTransactions';
import { useBudgets } from '../hooks/useBudgets';
import MonthlyComparisonChart from '../components/reports/MonthlyComparisonChart';
import ExpenseCategoryAnalysis from '../components/reports/ExpenseCategoryAnalysis';
import TrendsChart from '../components/reports/TrendsChart';
import ReportFilters from '../components/reports/ReportFilters';
import DataExport from '../components/reports/DataExport';

function Reports() {
  const { transactions, loading: loadingTransactions, error: transactionsError } = useTransactions();
  const { budgets, loading: loadingBudgets } = useBudgets();
  const [tabValue, setTabValue] = useState(0);
  const [filters, setFilters] = useState({
    period: 'month',
    year: new Date().getFullYear(),
    month: new Date().getMonth(),
    compareWith: 'previous',
    categories: []
  });
  const [filteredTransactions, setFilteredTransactions] = useState([]);

  // Obsługa zmiany zakładki
  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  // Obsługa zmiany filtrów
  const handleFilterChange = (newFilters) => {
    setFilters({ ...filters, ...newFilters });
  };

  // Filtrowanie transakcji na podstawie wybranych filtrów
  useEffect(() => {
    if (transactions && transactions.length > 0) {
      let filtered = [...transactions];
      
      // Filtrowanie według daty
      if (filters.period === 'month') {
        filtered = filtered.filter(transaction => {
          const date = new Date(transaction.date);
          return date.getFullYear() === filters.year && date.getMonth() === filters.month;
        });
      } else if (filters.period === 'year') {
        filtered = filtered.filter(transaction => {
          const date = new Date(transaction.date);
          return date.getFullYear() === filters.year;
        });
      }
      
      // Filtrowanie według kategorii, jeśli wybrano
      if (filters.categories && filters.categories.length > 0) {
        filtered = filtered.filter(transaction =>
          filters.categories.includes(transaction.category)
        );
      }
      
      setFilteredTransactions(filtered);
    } else {
      setFilteredTransactions([]);
    }
  }, [transactions, filters]);

  // Pobierz wszystkie unikalne kategorie z transakcji
  const getAllCategories = () => {
    if (!transactions || transactions.length === 0) return [];
    
    const categories = new Set();
    transactions.forEach(transaction => {
      if (transaction.category) {
        categories.add(transaction.category);
      }
    });
    
    return Array.from(categories);
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">Raporty i statystyki</Typography>
      </Box>
      
      {/* Filtry raportów */}
      <ReportFilters 
        filters={filters}
        onFilterChange={handleFilterChange}
        availableCategories={getAllCategories()}
      />
      
      {/* Zakładki */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
        <Tabs value={tabValue} onChange={handleTabChange}>
          <Tab label="Porównanie miesięczne" />
          <Tab label="Analiza kategorii" />
          <Tab label="Trendy" />
          <Tab label="Eksport danych" />
        </Tabs>
      </Box>
      
      {/* Komunikat o błędzie */}
      {transactionsError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {transactionsError}
        </Alert>
      )}
      
      {/* Zawartość zakładek */}
      {(loadingTransactions || loadingBudgets) ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Box sx={{ mt: 2 }}>
          {/* Porównanie miesięczne */}
          {tabValue === 0 && (
            <MonthlyComparisonChart 
              transactions={transactions || []}
              filters={filters}
            />
          )}
          
          {/* Analiza kategorii wydatków */}
          {tabValue === 1 && (
            <ExpenseCategoryAnalysis 
              transactions={filteredTransactions || []}
              allTransactions={transactions || []}
              filters={filters}
              budgets={budgets || []}
            />
          )}
          
          {/* Trendy */}
          {tabValue === 2 && (
            <TrendsChart 
              transactions={transactions || []}
              filters={filters}
            />
          )}
          
          {/* Eksport danych */}
          {tabValue === 3 && (
            <DataExport 
              transactions={filteredTransactions || []}
              filters={filters}
            />
          )}
        </Box>
      )}
    </Box>
  );
}

export default Reports;